import { ThemeProvider } from '@mui/material';
import type {} from '@mui/material/themeCssVarsAugmentation';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { SSEProvider } from 'react-hooks-sse';
import '@theme/main.scss';
import { EventSource } from '@common/eventsource';
import { CssBaseline, standardTheme } from '@theme';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/i18next';
import { routeTree } from './routeTree.gen';

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 } },
});

// Set up a Router instance
const router = createRouter({
  routeTree,
  context: {
    client: queryClient,
  },
});

// Register things for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Set the default theme to light otherwise it will default to prefered color scheme.
// [TODO] Remove this when we have implemented a complete dark theme.
window.localStorage.setItem('mui-mode', 'light');

const App = () => (
  <QueryClientProvider client={queryClient}>
    <SSEProvider source={() => new EventSource('/api/events')}>
      <ThemeProvider theme={standardTheme}>
        <CssBaseline />
        <I18nextProvider i18n={i18n}>
          <RouterProvider router={router} />
        </I18nextProvider>
      </ThemeProvider>
    </SSEProvider>
  </QueryClientProvider>
);

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
